import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, map, takeUntil } from 'rxjs';
import { SettingsService } from '@api/settings.service';
import { AuthenticationService } from '@shared/authentication/authentication.service';
import { BaseObject } from '@shared/base/base-object';
import { ThemeState } from '@shared/states/theme.state';
import { UserState } from '@shared/states/user.state';
import { BreakpointTypeEnum } from '@shared/types/screen-breakpoints';

@Component({
  selector: 'app-navigation-panel',
  templateUrl: './navigation-panel.component.html',
  styleUrls: ['./navigation-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.mobile]': 'settingsService.screenBreakpoint$.value === "XXSmall"',
    '[class.hidden]': '!isAvailablePage',
    '[class.no-bg]': 'isDashboardPage || isProfilePage',
    '[class.marketing-flow]': 'isMarketingFlow',
    '[class.wizard-flow]': 'isWizardPage',
  },
})
export class NavigationPanelComponent extends BaseObject {
  public get isAvailablePage(): boolean {
    return location.pathname !== '/login';
  }

  public get isDashboardPage(): boolean {
    return location.pathname === '/configurator/dashboard';
  }

  public get isWizardPage(): boolean {
    return location.pathname === '/wizard/order-create-flow';
  }

  public get isProfilePage(): boolean {
    return location.pathname === '/account/profile';
  }

  public get isMarketingFlow(): boolean {
    return (
      location.pathname === '/marketing/marketing' ||
      location.pathname === '/marketing/repair-composition'
    );
  }

  public readonly BreakpointTypeEnum = BreakpointTypeEnum;

  public colorState$ = new BehaviorSubject<'light' | 'dark'>('dark');

  constructor(
    public themeState: ThemeState,
    private authService: AuthenticationService,
    public settingsService: SettingsService,
    public userState: UserState,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    super();

    router.events
      .pipe(
        filter((val) => val instanceof NavigationEnd),
        map((val) => val as NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((val) => {
        this.colorState$.next(
          val.url === '/' ||
            val.url === '/configurator' ||
            val.url === '/configurator/dashboard' ||
            val.url === '/account' ||
            val.url === '/account/profile' ||
            val.url === '/marketing' ||
            val.url === '/marketing/marketing' ||
            val.url === '/marketing/repair-composition'
            ? 'dark'
            : 'light',
        );
        this.cdr.markForCheck();
      });

    this.colorState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.themeState.logo$.next(
        state === 'dark' || this.isMarketingFlow
          ? '/assets/img/logo/logo-light-2.svg'
          : '/assets/img/logo/logo-dark-2.svg',
      );

      this.themeState.burger$.next(
        state === 'dark' || this.isMarketingFlow
          ? '/assets/img/burger-light.svg'
          : '/assets/img/burger-dark.svg',
      );
    });
  }

  public onLogoClick(): void {
    this.router.navigate(['/']);
  }
}
